import { jsPDF } from "jspdf";

export default function generateSkierDaysReport(data) {

  // Extract yearStart, yearEnd, and reportData from data object
  const { yearStart, yearEnd, reportData, totalSkierDays } = data;

  const doc = new jsPDF('portrait');

  // Column definitions for consistent layout (defined early to avoid hoisting issues)
  const columns = [
    { header: "First Name", width: 45, key: 'firstName' },
    { header: "Last Name", width: 45, key: 'lastName' },
  ];

  // Function to wrap text within a column width (defined early to avoid hoisting issues)
  const wrapText = (doc, text, maxWidth) => {
    if (!text) return [''];

    const words = text.toString().split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textWidth = doc.getTextWidth(testLine);

      if (textWidth <= maxWidth - 4) { // Leave 4 units padding
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Single word is too long, truncate it
          lines.push(word.substring(0, Math.floor(word.length * maxWidth / textWidth)));
        }
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [''];
  };

  // Function to draw text in a cell with proper boundaries (defined early to avoid hoisting issues)
  const drawCellText = (doc, text, x, y, width, height) => {
    const lines = wrapText(doc, text, width);
    const lineHeight = 3;

    // Draw each line of text
    lines.forEach((line, index) => {
      if (index * lineHeight < height - 2) { // Make sure text fits in cell height
        doc.text(line, x + 2, y + 3 + (index * lineHeight));
      }
    });

    return lines.length * lineHeight;
  };

  const col1X = 15; // Left column X position
  const col2X = 105; // Right column X position
  let currentColumn = col1X; // Start with the first column
  let yPos = 10; // Initial Y position
  let contentStartY = 0; // Track where content starts for column alignment

  doc.setFontSize(18);
  doc.text("Skier Days Report", 105, yPos, { align: "center" });
  yPos += 7;

  // Add date range subtitle
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100); // Gray color for subtitle
  doc.text("Date Range: " + yearStart + " to " + yearEnd, 105, yPos, { align: "center" });
  doc.setTextColor(0, 0, 0); // Reset to black
  yPos += 15; // Adjust position after the title
  contentStartY = yPos; // Record where content starts for column alignment

  // Function to draw table headers using the column system
  const drawHeaders = (doc, y, x) => {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');

    let currentX = x;
    columns.forEach(col => {
      doc.setDrawColor(0);
      doc.setFillColor(211, 211, 211);
      doc.rect(currentX, y, col.width, 8, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.text(col.header, currentX + 2, y + 5.5);
      currentX += col.width;
    });

    doc.setFont('helvetica', 'normal');
    return y + 8;
  };

  // Function to render a skier using the column system
  const renderSkier = (doc, skier, y, x) => {
    doc.setFontSize(7);

    // Calculate the row height needed based on text wrapping
    let maxRowHeight = 6; // Minimum row height

    // First pass: calculate required height for this row
    columns.forEach(col => {
      let cellText = '';
      if (col.key === 'firstName') {
        cellText = skier.firstName || 'N/A';
      } else if (col.key === 'lastName') {
        cellText = skier.lastName || 'N/A';
      }

      const lines = wrapText(doc, cellText, col.width);
      const requiredHeight = Math.max(6, lines.length * 3 + 2);
      maxRowHeight = Math.max(maxRowHeight, requiredHeight);
    });

    // Note: Page/column switching is now handled at the date group level
    // so we don't need to check for overflow here

    // Draw cell borders and content
    let currentX = x;
    columns.forEach(col => {
      // Draw cell border
      doc.setDrawColor(200, 200, 200); // Light gray border
      doc.setFillColor(255, 255, 255);
      doc.rect(currentX, y, col.width, maxRowHeight, 'FD');

      // Get cell content
      let cellText = '';
      if (col.key === 'firstName') {
        cellText = skier.firstName || 'N/A';
      } else if (col.key === 'lastName') {
        cellText = skier.lastName || 'N/A';
      }

      // Set text color
      doc.setTextColor(0, 0, 0);

      // Draw cell content
      drawCellText(doc, cellText, currentX, y, col.width, maxRowHeight);

      currentX += col.width;
    });

    return y + maxRowHeight;
  };

  // Group data by date
  const groupedData = reportData.reduce((acc, skier) => {
    if (!acc[skier.date]) {
      acc[skier.date] = [];
    }
    acc[skier.date].push(skier);
    return acc;
  }, {});

  // Use backend calculated total if available, otherwise calculate from reportData
  const totalSkiers = totalSkierDays !== undefined ? totalSkierDays : reportData.length;

  // Function to calculate the total height needed for a complete date group
  const calculateDateGroupHeight = (date) => {
    let totalHeight = 0;
    totalHeight += 7; // Date header
    totalHeight += 8; // Table headers
    totalHeight += 2; // Spacing after headers

    // Calculate height for all skiers in this date group
    groupedData[date].forEach(skier => {
      // Calculate the row height needed based on text wrapping (same logic as renderSkier)
      let maxRowHeight = 6; // Minimum row height
      columns.forEach(col => {
        let cellText = '';
        if (col.key === 'firstName') {
          cellText = skier.firstName || 'N/A';
        } else if (col.key === 'lastName') {
          cellText = skier.lastName || 'N/A';
        }
        const lines = wrapText(doc, cellText, col.width);
        const requiredHeight = Math.max(6, lines.length * 3 + 2);
        maxRowHeight = Math.max(maxRowHeight, requiredHeight);
      });
      totalHeight += maxRowHeight;
    });

    totalHeight += 5; // Space before total
    totalHeight += 5; // Total line
    totalHeight += 10; // Space after total

    return totalHeight;
  };

  Object.keys(groupedData).forEach((date) => {
    // Calculate the total height needed for this complete date group
    const dateGroupHeight = calculateDateGroupHeight(date);

    // Check if we need to start a new page
    if (yPos + dateGroupHeight > 280) {
      // If we're in the first column, try switching to the second column
      if (currentColumn === col1X && contentStartY + dateGroupHeight <= 280) {
        currentColumn = col2X;
        yPos = contentStartY; // Start at the same Y position as first column content
      } else {
        // Start a new page
        doc.addPage();
        yPos = 10;
        doc.setFontSize(18);
        doc.text("Skier Days Report", 105, yPos, { align: "center" });
        yPos += 7;

        // Add date range subtitle
        doc.setFontSize(12);
        doc.setTextColor(100, 100, 100); // Gray color for subtitle
        doc.text("Date Range: " + yearStart + " to " + yearEnd, 105, yPos, { align: "center" });
        doc.setTextColor(0, 0, 0); // Reset to black
        yPos += 15;
        contentStartY = yPos; // Update content start for new page
        currentColumn = col1X;
      }
    }

    // Display date header for the new date group
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(`Date: ${date}`, currentColumn, yPos);
    yPos += 7;

    // Draw headers in the current column
    yPos = drawHeaders(doc, yPos, currentColumn);
    yPos += 2;

    // List each skier's info (no need for complex column switching since we handle it at date group level)
    groupedData[date].forEach(skier => {
      yPos = renderSkier(doc, skier, yPos, currentColumn);
    });

    // Add total for the date, aligned to the current column
    yPos += 5; // Add some space before total
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    let totalXPos = currentColumn + 70;
    doc.text(`Total Skiers: ${groupedData[date].length}`, totalXPos, yPos);
    yPos += 10;
  });

  // Ensure we're at the bottom of the last page for the final tally
  if (yPos + 10 > 280) {
    doc.addPage();
    yPos = 10;
  }

  // Display the overall total at the bottom
  doc.setFontSize(10);
  doc.text(`Overall Total Skiers: ${totalSkiers}`, 105, 290, { align: "center" });

  //const reportDate = new Date().toISOString().split('T')[0];
  doc.save(`skier-days-report-${yearStart}to${yearEnd}.pdf`);
}